import { useEffect, useState } from 'react';
import { Button, Form, Tab, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { LeadCloudFileType, TaskCreateFormType, TaskTemplateType } from '@econtractor/types';
import { useToast } from 'context/ToastContext';
import spinnerTitle from 'components/others/spinnerTitle';
import SpinnerBlock from 'components/others/SpinnerBlock';
import useNewTaskForm from './model/useNewTaskForm';
import NewTaskGeneralTab from './tabs/NewTaskGeneralTab';
import NewTaskAdditionalTab from './tabs/NewTaskAdditionalTab';
import NewTaskFilesTab from './tabs/NewTaskFilesTab';
import TaskAnswersEditor from '../shared/answers/TaskAnswersEditor';
import SolarIcon from 'components/icons/SolarIcon';
import { getTaskTemplate } from 'api/apiTask';
import Paginator from 'components/others/Paginator';
import { useTaskDirList } from '../TaskDir/model/useTaskDirList';
import { templateUserToForm } from '../TaskDir/model/templateUser';
import { applyTemplateTermDays } from '../shared/helpers/taskDueDateHelpers';
import NewTaskTemplatePicker from './NewTaskTemplatePicker';
import NewTaskFormBoundary from './NewTaskFormBoundary';
import RecurrentForm from '../shared/recurrent/RecurrentForm';
import { DateTime as DT } from 'luxon';

interface NewTaskFormProps {
    show: boolean;
    handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
    isSubmitting: boolean;
    setFieldValue: (field: string, value: unknown) => void;
    setValues: (values: TaskCreateFormType) => void;
    values: TaskCreateFormType;
    pendingCloudFiles: File[];
    setPendingCloudFiles: React.Dispatch<React.SetStateAction<File[]>>;
    cloudProgress: number | boolean;
    taskForLocked?: boolean;
}

const hasText = (html: string): boolean => Boolean(String(html || '').replace(/<[^>]*>/g, '').trim());

const NewTaskForm = ({
    show,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    setValues,
    values,
    pendingCloudFiles,
    setPendingCloudFiles,
    cloudProgress,
    taskForLocked = false,
}: NewTaskFormProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { showToast } = useToast();
    const [pickerOpen, setPickerOpen] = useState(false);
    const [picking, setPicking] = useState(false);
    const [templateFiles, setTemplateFiles] = useState<LeadCloudFileType[]>([]);
    const [holdDescription, setHoldDescription] = useState(false);
    const [formAttempt, setFormAttempt] = useState(0);
    const PaginatorAny = Paginator as any;
    const {
        searchInput,
        setSearchInput,
        categoryId,
        setFilterCategoryId,
        setPage,
        setOnPage,
        paginator,
        pageSizeKey,
        templates,
        showSpinner,
    } = useTaskDirList(pickerOpen);

    const {
        showDescription,
        setShowDescription,
        activeTab,
        setActiveTab,
        canAssignMyself,
        assignMyself,
        handleEndDateChange,
        handleStartDateChange,
        handleWithTimeChange,
        handleStartTimeChange,
        handleEndTimeChange,
        handleMultiDayChange,
    } = useNewTaskForm({ values, setFieldValue });

    useEffect(() => {
        setPickerOpen(false);
        setTemplateFiles([]);
        setPicking(false);
        setHoldDescription(false);
        setFormAttempt(0);
    }, [show]);

    useEffect(() => {
        if (!holdDescription) return;
        setHoldDescription(false);
    }, [holdDescription]);

    const closePicker = () => {
        setHoldDescription(true);
        setPickerOpen(false);
    };

    const handlePick = async (template: TaskTemplateType) => {
        setPicking(true);
        const card = await getTaskTemplate(template.ID);
        setPicking(false);
        if (!card) {
            showToast(t('common:messError'), 'danger');
            return;
        }

        const termDates = applyTemplateTermDays(values.dateStart, Number(card.termDays));

        setValues({
            ...values,
            ...(termDates ?? {}),
            templateId: card.ID,
            taskTypeId: card.taskType,
            categoryId: card.categoryId ?? null,
            taskName: card.taskName,
            taskInfo: card.taskDescription || '',
            executor: templateUserToForm(card.executorUser),
            controller: templateUserToForm(card.controllerUser),
            reserveExecutorTime: Boolean(card.reserveExecutorTime),
            executorCanChangeTerm: Boolean(card.executorCanChangeTerm),
            noEarlyStart: Boolean(card.noEarlyStart),
            recurrentEnabled: Boolean(card.recurrentEvent),
            recurrentData: card.recurrentData || null,
            recurrentAllowOverlap: Boolean(card.recurrentAllowOverlap),
            pay: Boolean(card.pay),
            payAmount: card.pay ? Number(card.payAmount || 0) : null,
            payUsd: card.payUsd !== false,
            showPayToExecutor: Boolean(card.pay && card.showPayToExecutor),
            answers: card.answers || [],
        });
        setTemplateFiles(card.files || []);
        setShowDescription(hasText(card.taskDescription || ''));
        setActiveTab('general');
        closePicker();
    };

    const retryForm = () => {
        setHoldDescription(true);
        setFormAttempt((attempt) => attempt + 1);
    };

    return (
        <Form className="notranslate d-flex flex-column h-100" translate="no" onSubmit={handleSubmit}>
            <NewTaskFormBoundary
                resetKey={`${show ? 1 : 0}-${formAttempt}`}
                fallback={
                    <div className="p-3">
                        <p className="text-danger mb-3">{t('common:messError')}</p>
                        <Button type="button" variant="light-secondary" onClick={retryForm}>
                            {t('common:buttons.reset')}
                        </Button>
                    </div>
                }
            >
            <div className="off-new-task-form d-flex flex-column h-100">
                <div className="off-new-task-content px-3 position-relative">
                    <SpinnerBlock show={isSubmitting || picking} onlyBlur={true} />

                    <div className="mb-3">
                        <button
                            type="button"
                            className="btn btn-link text-primary p-0 align-baseline"
                            disabled={isSubmitting}
                            onClick={() => {
                                if (pickerOpen) {
                                    closePicker();
                                    return;
                                }
                                setPickerOpen(true);
                            }}
                        >
                            <SolarIcon
                                name={pickerOpen ? 'x-circle' : 'bookmark-star'}
                                className="me-1 fs-14"
                            />
                            {pickerOpen ? t('directory.btnCancel') : t('directory.btnPick')}
                        </button>
                        <div className="text-muted fs-12">{t('directory.hintPick')}</div>
                    </div>

                    {pickerOpen ? (
                        <NewTaskTemplatePicker
                            searchInput={searchInput}
                            setSearchInput={setSearchInput}
                            categoryId={categoryId}
                            setFilterCategoryId={setFilterCategoryId}
                            templates={templates}
                            showSpinner={showSpinner}
                            onSelect={(row) => void handlePick(row)}
                        />
                    ) : (
                        <Tabs
                            variant="underline"
                            activeKey={activeTab}
                            onSelect={(tab) => tab && setActiveTab(tab)}
                            className="mb-3 invoice-tab border-bottom"
                        >
                            <Tab
                                mountOnEnter
                                unmountOnExit
                                eventKey="general"
                                title={
                                    <>
                                        <SolarIcon name="gear" className="me-2 fs-14" />
                                        {t('tabGeneral')}
                                    </>
                                }
                            >
                                <NewTaskGeneralTab
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    isSubmitting={isSubmitting}
                                    showDescription={showDescription}
                                    setShowDescription={setShowDescription}
                                    canAssignMyself={canAssignMyself}
                                    assignMyself={assignMyself}
                                    handleEndDateChange={handleEndDateChange}
                                    handleStartDateChange={handleStartDateChange}
                                    handleWithTimeChange={handleWithTimeChange}
                                    handleStartTimeChange={handleStartTimeChange}
                                    handleEndTimeChange={handleEndTimeChange}
                                    handleMultiDayChange={handleMultiDayChange}
                                    taskForLocked={taskForLocked}
                                    editorReady={!holdDescription}
                                />
                            </Tab>

                            <Tab
                                mountOnEnter
                                unmountOnExit
                                eventKey="additional"
                                title={
                                    <>
                                        <SolarIcon name="card-list" className="me-2 fs-14" />
                                        {t('tabAdditional')}
                                    </>
                                }
                            >
                                <NewTaskAdditionalTab
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    isSubmitting={isSubmitting}
                                />
                            </Tab>

                            <Tab
                                mountOnEnter
                                unmountOnExit
                                eventKey="answers"
                                title={
                                    <>
                                        <SolarIcon name="list-ul" className="me-2 fs-14" />
                                        {t('tabAnswers')}
                                    </>
                                }
                            >
                                <TaskAnswersEditor
                                    value={values.answers || []}
                                    disabled={isSubmitting}
                                    onChange={(next) => setFieldValue('answers', next)}
                                />
                            </Tab>

                            <Tab
                                mountOnEnter
                                unmountOnExit
                                eventKey="files"
                                title={
                                    <>
                                        <SolarIcon name="paperclip" className="me-2 fs-14" />
                                        {t('tabFiles')}
                                        {pendingCloudFiles.length > 0
                                            ? ` (${pendingCloudFiles.length})`
                                            : ''}
                                    </>
                                }
                            >
                                <NewTaskFilesTab
                                    isSubmitting={isSubmitting}
                                    cloudProgress={cloudProgress}
                                    pendingCloudFiles={pendingCloudFiles}
                                    setPendingCloudFiles={setPendingCloudFiles}
                                    templateFiles={templateFiles}
                                />
                            </Tab>

                            <Tab
                                mountOnEnter
                                unmountOnExit
                                eventKey="recurrent"
                                title={
                                    <>
                                        <SolarIcon name="arrow-repeat" className="me-2 fs-14" />
                                        {t('tabRecurrent')}
                                    </>
                                }
                            >
                                <RecurrentForm
                                    idPrefix="new-task"
                                    value={{
                                        enabled: values.recurrentEnabled,
                                        data: values.recurrentData,
                                        allowOverlap: values.recurrentAllowOverlap,
                                    }}
                                    disabled={isSubmitting}
                                    showAllowOverlap={values.reserveExecutorTime}
                                    monthDayHint={DT.fromMillis(values.dateEnd || Date.now(), { zone: 'local' }).day}
                                    onChange={(next) => {
                                        setFieldValue('recurrentEnabled', next.enabled);
                                        setFieldValue('recurrentData', next.data);
                                        setFieldValue('recurrentAllowOverlap', next.allowOverlap);
                                    }}
                                />
                            </Tab>
                        </Tabs>
                    )}
                </div>

                {pickerOpen && paginator ? (
                    <div className="p-3 offcanvas-footer off-new-task-footer">
                        <PaginatorAny
                            paginator={paginator}
                            storageKey={pageSizeKey}
                            onChangePage={(nextPage: number) => setPage(nextPage)}
                            onChangeOnPage={(nextOnPage: number) => {
                                setOnPage(nextOnPage);
                                setPage(1);
                            }}
                        />
                    </div>
                ) : null}
                {!pickerOpen ? (
                    <div className="p-3 offcanvas-footer off-new-task-footer">
                        <div className="d-flex justify-content-end">
                            <Button
                                type="submit"
                                variant="info"
                                disabled={isSubmitting}
                                className="min100px"
                            >
                                {spinnerTitle(
                                    <>
                                        <SolarIcon name="check2-all" className="fs-14 me-2" />
                                        {t('common:buttons.create')}
                                    </>,
                                    isSubmitting,
                                    t('common:buttons.creating'),
                                )}
                            </Button>
                        </div>
                    </div>
                ) : null}
            </div>
            </NewTaskFormBoundary>
        </Form>
    );
};

export default NewTaskForm;
